// Ankle Joint Template Configuration

export const ankleTemplate = {
  id: 'ANKLE',
  name: 'Ankle Joint',
  icon: '🦶',
  dominantSide: 'leg',

  fields: {
    dominantLeg: {
      type: 'radio',
      label: 'Which leg do you kick a ball with?',
      options: ['Right', 'Left'],
      required: true
    },

    injuredSide: {
      type: 'radio',
      label: 'Which ankle is the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did you injure your ankle?',
      required: true
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Describe how the injury occurred:',
      placeholder: 'Please describe the circumstances of your injury',
      rows: 3
    },

    mechanism: {
      type: 'checkbox-multiple',
      label: 'How did you injure your ankle?',
      options: [
        'Rolled ankle inward',
        'Rolled ankle outward',
        'Twisting',
        'Fell from a height',
        'Got hit directly on the ankle',
        'Playing sport',
        'Car accident',
        'It came on gradually (no specific injury)',
        'Other'
      ]
    },

    mechanismOther: {
      type: 'text',
      label: 'Please describe what happened:',
      placeholder: 'Tell us how you were injured',
      showWhen: { field: 'mechanism', includes: 'Other' }
    },

    painSite: {
      type: 'text',
      label: 'Where is your pain?',
      placeholder: 'e.g., Inside of ankle, outside, front, back, all over',
      section: 'pain'
    },

    painRadiation: {
      type: 'text',
      label: 'Does your pain spread anywhere else?',
      placeholder: 'e.g., To foot, toes, leg, knee',
      section: 'pain'
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Sharp', 'Aching', 'Throbbing', 'Burning', 'Stabbing'],
      section: 'pain'
    },

    aggravatingFactors: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Walking',
        'Running',
        'Standing',
        'Stairs',
        'Uneven ground',
        'Putting weight on it',
        'First steps in the morning',
        'End of the day',
        'Other'
      ],
      section: 'pain'
    },

    aggravatingFactorsOther: {
      type: 'text',
      label: 'Other aggravating factors:',
      placeholder: 'Please specify',
      showWhen: { field: 'aggravatingFactors', includes: 'Other' }
    },

    relievingFactors: {
      type: 'checkbox-multiple',
      label: 'What makes your pain better?',
      options: [
        'Rest',
        'Putting your foot up',
        'Ice',
        'Compression bandage',
        'Pain medication',
        'Ankle brace or support',
        'Taping',
        'Other'
      ],
      section: 'pain'
    },

    relievingFactorsOther: {
      type: 'text',
      label: 'Other relieving factors:',
      placeholder: 'Please specify',
      showWhen: { field: 'relievingFactors', includes: 'Other' }
    },

    associatedSymptoms: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these other symptoms?',
      options: [
        'Swelling',
        'Stiffness',
        'Ankle gives way or feels wobbly',
        'Numbness or tingling',
        'Clicking or locking',
        'Weakness'
      ],
      section: 'pain'
    },

    // Treatment fields
    hadPhysio: {
      type: 'radio',
      label: 'Have you had physiotherapy for this injury?',
      options: ['Yes', 'No']
    },

    physioWhere: {
      type: 'text',
      label: 'Where did you have physiotherapy?',
      placeholder: 'e.g., PhysioFirst Hamilton, ACC Physio Clinic',
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioWho: {
      type: 'text',
      label: 'Who was your physiotherapist?',
      placeholder: 'e.g., John Smith, Sarah Jones',
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioWhat: {
      type: 'textarea',
      label: 'What treatment did they provide?',
      placeholder: 'e.g., Exercises, massage, dry needling, ultrasound, taping',
      rows: 2,
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioHelped: {
      type: 'radio',
      label: 'Did the physiotherapy help?',
      options: ['Yes, significantly', 'Yes, somewhat', 'No improvement', 'Made it worse'],
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    medications: {
      type: 'textarea',
      label: 'What medications have you tried?',
      placeholder: 'e.g., Paracetamol, ibuprofen, voltaren - did they help?',
      rows: 2
    },

    imaging: {
      type: 'radio',
      label: 'Have you had any imaging (X-ray, MRI, ultrasound)?',
      options: ['Yes', 'No']
    },

    imagingType: {
      type: 'checkbox-multiple',
      label: 'What type of imaging have you had?',
      options: ['X-ray', 'MRI', 'Ultrasound', 'CT scan', 'Other'],
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhere: {
      type: 'text',
      label: 'Where was the imaging done?',
      placeholder: 'e.g., Waikato Hospital, Pacific Radiology',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhen: {
      type: 'date',
      label: 'When was the imaging done?',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingResults: {
      type: 'textarea',
      label: 'What were the results?',
      placeholder: 'e.g., Normal, ligament tear, fracture',
      rows: 2,
      showWhen: { field: 'imaging', includes: 'Yes' }
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true,
      customFields: ['dominantLeg']
    },
    {
      id: 'injury',
      title: '2. How You Injured Your Ankle',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'mechanism', 'mechanismOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painSite', 'painCharacter', 'painSeverity', 'painRadiation',
               'aggravatingFactors', 'aggravatingFactorsOther', 'relievingFactors', 'relievingFactorsOther', 'associatedSymptoms']
    },
    {
      id: 'swelling',
      title: '4. Swelling / Bruising',
      component: 'SwellingSection',
      enabled: true,
      detailed: true,
      fields: [
        {
          id: 'swellingDuration',
          label: 'When did the swelling start?',
          type: 'text',
          placeholder: 'e.g., Straight away, a few hours later, it\'s been there for weeks'
        },
        {
          id: 'swellingSite',
          label: 'Where is the swelling?',
          type: 'text',
          placeholder: 'e.g., Inside of ankle, outside, front, all over'
        },
        {
          id: 'swellingProgression',
          label: 'Is the swelling:',
          type: 'select',
          options: ['', 'Getting worse', 'Getting better', 'Staying the same', 'Comes and goes']
        },
        {
          id: 'fluctuationWithActivity',
          label: 'Does the swelling change with activity?',
          type: 'radio',
          options: ['Yes', 'No']
        },
        {
          id: 'bruising',
          label: 'Do you have any bruising?',
          type: 'radio',
          options: ['Yes', 'No']
        }
      ]
    },
    {
      id: 'instability',
      title: '5. Ankle Giving Way',
      component: 'InstabilitySection',
      enabled: true,
      type: 'ankle',
      fields: [
        {
          id: 'givingWayEpisodes',
          type: 'radio',
          label: 'Does your ankle give way or feel like it might collapse?',
          options: ['Yes', 'No']
        },
        {
          id: 'givingWayFrequency',
          type: 'text',
          label: 'How often does this happen?',
          placeholder: 'e.g., Every day, once a week, only when playing sport',
          conditional: { field: 'givingWayEpisodes', value: 'Yes' }
        },
        {
          id: 'triggeringActivities',
          type: 'textarea',
          label: 'What activities make your ankle give way?',
          placeholder: 'e.g., Running, sports, stairs, uneven ground, just walking',
          rows: 2,
          conditional: { field: 'givingWayEpisodes', value: 'Yes' }
        },
        {
          id: 'braceUse',
          type: 'radio',
          label: 'Do you wear an ankle brace or support?',
          options: ['Yes', 'No']
        },
        {
          id: 'braceDetails',
          type: 'text',
          label: 'What type of brace do you use?',
          placeholder: 'e.g., Lace-up brace, hard brace, tape',
          conditional: { field: 'braceUse', value: 'Yes' }
        },
        {
          id: 'fearOfInstability',
          type: 'radio',
          label: 'Do you avoid activities because you\'re worried your ankle will give way?',
          options: ['Yes', 'No']
        }
      ]
    },
    {
      id: 'stiffness',
      title: '6. Stiffness / Movement',
      component: 'StiffnessSection',
      enabled: true,
      movements: [
        { id: 'dorsiflexion', label: 'Pulling foot up towards your shin', icon: '🖐️' },
        { id: 'plantarflexion', label: 'Pointing foot down', icon: '🖐️' },
        { id: 'inversion', label: 'Turning sole of foot inward', icon: '↪️' },
        { id: 'eversion', label: 'Turning sole of foot outward', icon: '↩️' }
      ],
      morningStiffness: true
    },
    {
      id: 'functional',
      title: '7. What You Can and Can\'t Do',
      component: 'FunctionalLimitationSection',
      enabled: true,
      activities: [
        'Walking long distances',
        'Running or sports',
        'Going up stairs',
        'Going down stairs',
        'Walking on uneven ground',
        'Wearing certain shoes (like heels or boots)',
        'Balance activities',
        'Hopping on your sore leg',
        'Squatting',
        'Kneeling'
      ]
    },
    {
      id: 'treatment',
      title: '8. Treatment So Far',
      component: 'GenericSection',
      enabled: true,
      fields: ['hadPhysio', 'physioWhere', 'physioWho', 'physioWhat', 'physioHelped', 'medications', 'imaging', 'imagingType', 'imagingWhere', 'imagingWhen', 'imagingResults']
    },
    {
      id: 'pastHistory',
      title: '9. Your Ankle History',
      component: 'PastHistorySection',
      enabled: true,
      specificQuestions: [
        {
          id: 'previousAnkleInjuries',
          label: 'Have you injured this ankle before?',
          type: 'textarea',
          placeholder: 'e.g., Sprains, broken bones, Achilles problems',
          rows: 3
        },
        {
          id: 'numberOfPreviousInjuries',
          label: 'How many times have you sprained this ankle?',
          type: 'text',
          placeholder: 'e.g., Once, twice, many times'
        },
        {
          id: 'recurrentSprains',
          label: 'Do you keep spraining the same ankle?',
          type: 'radio',
          options: ['Yes', 'No']
        },
        {
          id: 'previousSurgeries',
          label: 'Have you had any ankle surgery or been in a cast/boot?',
          type: 'textarea',
          placeholder: 'e.g., Ligament repair, broken bone fixed with screws',
          rows: 2
        },
        {
          id: 'otherFootProblems',
          label: 'Do you have any other foot or ankle problems?',
          type: 'textarea',
          placeholder: 'e.g., Flat feet, high arches, bunions, heel pain',
          rows: 2
        }
      ]
    },
    {
      id: 'medical',
      title: '10. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true,
      additionalFields: [
        {
          id: 'footwearHabits',
          label: 'What shoes do you usually wear?',
          type: 'text',
          placeholder: 'e.g., High heels, work boots, running shoes, often barefoot'
        }
      ]
    },    {
      id: 'exerciseVitalSign',
      title: '11. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '12. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Rolled ankle inward': 'an inversion injury',
      'Rolled ankle outward': 'an eversion injury',
      'Twisting': 'a twisting mechanism',
      'Fell from a height': 'a fall from height',
      'Got hit directly on the ankle': 'direct trauma',
      'Playing sport': 'a sports-related injury',
      'Car accident': 'a road traffic accident',
      'It came on gradually (no specific injury)': 'a gradual onset'
    },

    examinationGuidance: `
Based on the clinical information provided, examination findings would typically include:

INSPECTION: Assessment of swelling distribution (medial vs lateral), bruising pattern, alignment, deformity, gait pattern.

PALPATION: Tenderness over lateral ligament complex (ATFL, CFL), medial ligaments (deltoid), syndesmosis, malleoli, base of 5th metatarsal, Achilles tendon, anterior joint line.

RANGE OF MOTION:
- Dorsiflexion and plantarflexion
- Inversion and eversion
- Comparison with contralateral ankle

STRENGTH TESTING:
- Ankle dorsiflexors and plantarflexors
- Evertors and invertors
- Assessment of push-off strength

SPECIAL TESTS:
- Anterior drawer test (ATFL integrity)
- Talar tilt test (CFL integrity)
- Squeeze test (syndesmosis)
- External rotation test (syndesmosis)
- Thompson test (Achilles integrity)

FUNCTIONAL ASSESSMENT:
- Single leg balance
- Single leg heel raise
- Gait assessment
- Hop test (if appropriate)

NEUROVASCULAR: Dorsalis pedis and posterior tibial pulses, sensation in superficial peroneal, deep peroneal, and sural nerve distributions
`,

    commonDiagnoses: [
      'Lateral ankle sprain (ATFL +/- CFL)',
      'Chronic ankle instability',
      'High ankle sprain (syndesmosis injury)',
      'Medial ankle sprain (deltoid ligament)',
      'Ankle impingement (anterior or posterior)',
      'Peroneal tendinopathy',
      'Achilles tendinopathy',
      'Osteochondral lesion of talus',
      'Ankle arthritis (post-traumatic)',
      'Sinus tarsi syndrome'
    ]
  }
};

export default ankleTemplate;
